import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  FileEdit,
  Folder,
  GraduationCap,
  ShieldCheck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Kicker } from '@/design-system';
import {
  BuildDocProgressCells,
  BuildDocProgressHeader,
} from '@/shared/BuildDocProgressCells';
import {
  groupBuildDocsByModule,
  loaderForBuildDoc,
  renderBuildDoc,
  subGroupsFor,
} from '@/shared/build-docs';
import { SURFACE_BUILD_PROGRESS, getSurfaceProgress } from '@/shared/surface-build-progress';

// DocsLibraryScene — `/docs/logic` reference tab. Sidebar groups every
// `docs/logic/*.md` by module; canvas renders the active doc in full.
// Mirrors the drawer's typography (.help-anchor-body in global.css) so the
// two surfaces feel like the same document, just at different scales.
//
// Plan: docs/plans/build-docs-tab.md. Phase A — scene + sidebar + canvas.
//
// 2026-05-18 (#31, Nirman) — sidebar refactored from a flat 47-doc list
// into a collapsible two-level hierarchy:
//
//   • Module rows (Workspace · Studies · Documents · Reporting · Governance
//     · Infrastructure) are collapsible disclosures with a chevron, a small
//     Lucide icon for visual identity, and a count badge.
//   • Modules with a SUB_GROUP_DEFS entry in build-docs.ts (currently
//     Reporting + Governance) render their docs under sub-headings
//     (Composition / Writing / Discourse for Reporting; Quality / TEG
//     workflow for Governance). Other modules render flat.
//   • Default expansion: only the active doc's module starts open.
//   • Doc title rendering — the leaf segment of the path (so
//     `reporting/discourse/citation-chip` reads as "Citation chip", not
//     "Discourse/citation chip" as before).

const MODULE_ORDER = [
  'Workspace',
  'Studies',
  'Documents',
  'Reporting',
  'Governance',
  'Infrastructure',
];

const MODULE_ICON: Record<string, LucideIcon> = {
  Workspace: Briefcase,
  Studies: GraduationCap,
  Documents: Folder,
  Reporting: FileEdit,
  Governance: ShieldCheck,
  Infrastructure: Wrench,
};

function prettyDocTitle(key: string): string {
  // "studies/action-tracker"                  → "Action tracker"
  // "reporting/discourse/citation-chip"       → "Citation chip"  (leaf only)
  const leaf = key.split('/').pop() ?? key;
  const spaced = leaf.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function DocsLibraryScene() {
  const params = useParams<{ module?: string; doc?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const groups = useMemo(() => groupBuildDocsByModule(), []);
  const orderedModules = useMemo(() => {
    const present = Object.keys(groups);
    const known = MODULE_ORDER.filter((m) => present.includes(m));
    const extras = present.filter((m) => !MODULE_ORDER.includes(m)).sort();
    return [...known, ...extras];
  }, [groups]);

  const activeKey = params.module && params.doc ? `${params.module}/${params.doc}` : null;
  const activeModuleCapitalised = useMemo(() => {
    if (!params.module) return null;
    return params.module.charAt(0).toUpperCase() + params.module.slice(1);
  }, [params.module]);

  // Per-module expansion state. Default: only the active module's row is
  // open. Updates on navigation so deep-linking to a doc auto-opens its
  // parent module (without collapsing what the user already opened).
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => ({
    [activeModuleCapitalised ?? orderedModules[0] ?? 'Workspace']: true,
  }));

  // Auto-expand the active module when the route changes (e.g. user picks
  // a doc from a different module via search). Doesn't collapse anything;
  // additive only.
  useEffect(() => {
    if (!activeModuleCapitalised) return;
    setExpanded((prev) =>
      prev[activeModuleCapitalised] ? prev : { ...prev, [activeModuleCapitalised]: true },
    );
  }, [activeModuleCapitalised]);

  function toggleModule(mod: string) {
    setExpanded((prev) => ({ ...prev, [mod]: !prev[mod] }));
  }

  // Auto-select the first doc if none is in the URL.
  useEffect(() => {
    if (activeKey) return;
    const firstModule = orderedModules[0];
    const firstKey = firstModule ? groups[firstModule]?.[0] : undefined;
    if (firstKey) {
      navigate(`/docs/logic/${firstKey}`, { replace: true });
    }
  }, [activeKey, orderedModules, groups, navigate]);

  return (
    <div
      data-comment-anchor="DocsLibraryScene"
      className="flex-1 flex overflow-hidden"
    >
      <aside
        className="overflow-y-auto scroll-soft hairline-r flex-shrink-0"
        style={{
          width: 360,
          background: 'var(--surface-1)',
          padding: '20px 14px 40px',
        }}
      >
        <div
          className="flex items-center gap-2"
          style={{ padding: '0 8px 4px', color: 'var(--ink-tertiary)' }}
        >
          <BookOpen size={14} strokeWidth={1.75} />
          <Kicker>Build docs</Kicker>
        </div>
        <div
          style={{
            padding: '0 8px 8px',
            fontSize: 10,
            color: 'var(--ink-tertiary)',
            lineHeight: 1.4,
          }}
        >
          Build audit · {SURFACE_BUILD_PROGRESS.updated}
          {SURFACE_BUILD_PROGRESS.context ? (
            <>
              <br />
              {SURFACE_BUILD_PROGRESS.context}
            </>
          ) : null}
        </div>
        <BuildDocProgressHeader />

        {orderedModules.map((mod) => {
          const keys = groups[mod] ?? [];
          const isExpanded = expanded[mod] === true;
          const Icon = MODULE_ICON[mod] ?? Folder;
          const subGroups = isExpanded ? subGroupsFor(mod, keys) : null;
          return (
            <div key={mod} style={{ marginBottom: '4px' }}>
              <button
                type="button"
                onClick={() => toggleModule(mod)}
                className="flex items-center w-full text-left"
                style={{
                  padding: '6px 8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 4,
                  color: 'var(--ink-secondary)',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.10em',
                  fontWeight: 600,
                  gap: 6,
                  transition: 'background 100ms ease, color 100ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--surface-2)';
                  e.currentTarget.style.color = 'var(--ink-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--ink-secondary)';
                }}
                aria-expanded={isExpanded}
                aria-controls={`docs-module-${mod}`}
              >
                {isExpanded ? (
                  <ChevronDown size={11} aria-hidden style={{ flexShrink: 0 }} />
                ) : (
                  <ChevronRight size={11} aria-hidden style={{ flexShrink: 0 }} />
                )}
                <Icon
                  size={12}
                  aria-hidden
                  style={{ color: 'var(--ink-tertiary)', flexShrink: 0 }}
                />
                <span style={{ flex: 1 }}>{mod}</span>
                <span
                  className="tabular"
                  style={{
                    color: 'var(--ink-tertiary)',
                    fontWeight: 500,
                    fontSize: 10,
                    letterSpacing: '0.04em',
                  }}
                >
                  {keys.length}
                </span>
              </button>

              {isExpanded ? (
                <div id={`docs-module-${mod}`} style={{ marginTop: 2, paddingBottom: 4 }}>
                  {subGroups ? (
                    subGroups.map((sub) => (
                      <div key={sub.label} style={{ marginBottom: 6 }}>
                        <div
                          style={{
                            padding: '4px 8px 4px 26px',
                            fontSize: 9,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: 'var(--ink-tertiary)',
                            fontWeight: 600,
                          }}
                        >
                          {sub.label}
                          <span
                            className="tabular"
                            style={{ marginLeft: 6, opacity: 0.7 }}
                          >
                            · {sub.keys.length}
                          </span>
                        </div>
                        {sub.keys.map((key) => (
                          <DocLink
                            key={key}
                            docKey={key}
                            isActive={key === activeKey}
                            hash={location.hash}
                            indent={26}
                          />
                        ))}
                      </div>
                    ))
                  ) : (
                    keys.map((key) => (
                      <DocLink
                        key={key}
                        docKey={key}
                        isActive={key === activeKey}
                        hash={location.hash}
                        indent={26}
                      />
                    ))
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </aside>

      <DocsLibraryCanvas activeKey={activeKey} />
    </div>
  );
}

function DocLink({
  docKey,
  isActive,
  hash,
  indent,
}: {
  docKey: string;
  isActive: boolean;
  hash: string;
  indent: number;
}) {
  const progress = getSurfaceProgress(docKey);
  const delta =
    progress?.previous !== undefined ? progress.total - progress.previous.total : null;
  const progressTitle = progress
    ? `DM ${progress.dataModel}% · BE ${progress.backend}% · UI ${progress.ui}% · Total ${progress.total}%${
        delta !== null && delta !== 0
          ? ` (${delta > 0 ? '+' : ''}${delta} vs main)`
          : delta === 0
            ? ' (unchanged vs main)'
            : ''
      }`
    : undefined;

  return (
    <Link
      to={`/docs/logic/${docKey}${hash || ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: `5px 8px 5px ${indent}px`,
        borderRadius: 4,
        fontSize: 13,
        color: isActive ? 'var(--ink-primary)' : 'var(--ink-secondary)',
        background: isActive ? 'var(--surface-2)' : 'transparent',
        textDecoration: 'none',
        fontWeight: isActive ? 500 : 400,
        transition: 'background 100ms ease, color 100ms ease',
      }}
      title={[progressTitle, `docs/logic/${docKey}.md`].filter(Boolean).join(' — ')}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {prettyDocTitle(docKey)}
      </span>
      <BuildDocProgressCells docKey={docKey} />
    </Link>
  );
}

function DocsLibraryCanvas({ activeKey }: { activeKey: string | null }) {
  const [body, setBody] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { hash } = useLocation();

  const loader = useMemo(
    () => (activeKey ? loaderForBuildDoc(activeKey) : undefined),
    [activeKey],
  );

  useEffect(() => {
    if (!activeKey) {
      setBody(null);
      setError(null);
      return;
    }
    if (!loader) {
      setError(`No doc registered at docs/logic/${activeKey}.md`);
      return;
    }
    let cancelled = false;
    setBody(null);
    setError(null);
    loader()
      .then((raw) => {
        if (cancelled) return;
        setBody(renderBuildDoc(raw));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load doc');
      });
    return () => {
      cancelled = true;
    };
  }, [activeKey, loader]);

  // Scroll to #<slug> on hash change after the body renders.
  useEffect(() => {
    if (!body || !hash) return;
    const id = hash.replace(/^#/, '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [body, hash]);

  return (
    <section
      className="flex-1 overflow-y-auto scroll-soft"
      style={{ padding: '32px 48px 96px', background: 'var(--surface-base)' }}
    >
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <div
          style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontWeight: 600,
            color: 'var(--ink-tertiary)',
            marginBottom: '6px',
          }}
        >
          {activeKey
            ? `docs/logic/${activeKey}.md`
            : 'Build docs library'}
        </div>

        {error && (
          <div style={{ color: 'var(--state-blocked)', fontSize: '13px' }}>
            {error}
          </div>
        )}
        {!error && body === null && activeKey && (
          <div style={{ color: 'var(--ink-tertiary)', fontSize: '12px' }}>
            Loading…
          </div>
        )}
        {!error && body !== null && (
          <div
            className="help-anchor-body"
            style={{
              color: 'var(--ink-secondary)',
              fontSize: '14px',
              lineHeight: 1.65,
            }}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        )}
      </div>
    </section>
  );
}
